import { isArray } from "lodash";
import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { usePymbleApi } from "./usePymbleApi";

// const MY_ROOMS_END_POINT = `${api.dtAdmin}/room/timetable`;
const MY_ROOMS_END_POINT = "/timetable/room/timetable/";

export type RoomResourceDetail = {
  id: string;
  name: string;
  color: string;
  BuildingId?: number;
  Capacity: number;
  NameShort: string;
  NameLong: string;
  children?: RoomResourceDetail[];
  AllowRoomSwap?: boolean;
};

/**
 * Hook for separate loading of a class list
 * for components
 */
export function useRoomResources() {
  const [myRoomList, setMyRoomList] = useState<any>([]);
  const [myRoomListLoaded, setMyRoomListLoaded] = useState(false);

  const queryClient = useQueryClient();

  const { callApiS2: callApi, isAuthenticated } = usePymbleApi();

  // Load the contact list from api
  let queryKey = ["my-room-resources"];

  const { isLoading, isError, data, error } = useQuery({
    queryKey,
    queryFn: () =>
      callApi("GET", MY_ROOMS_END_POINT + "?qtype=resources"),
    enabled: isAuthenticated,
  });

  // When the data is loaded, set the state
  useEffect(() => {
    if (data && data.length) {
      let list = preProcessRoomList(data);

      // Sort by name
      list = sortRoomList(list);

      // Filter out rooms with AllowRoomSwap = false
      list = list.filter((room: RoomResourceDetail) => {
        return room?.AllowRoomSwap ?? true;
      });

      if (list) {
        setMyRoomList(list);
      }
      setMyRoomListLoaded(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data && !isLoading]);

  const refreshMyRoomList = async () => {
    queryClient.invalidateQueries({ queryKey });
  };

  /**
   * Sort the room list by name.
   * Also sorts the children of each element by name.
   *
   * @param data Array of RoomResourceDetail
   * @returns Array of RoomResourceDetail
   */
  const sortRoomList = (data: RoomResourceDetail[]) => {
    // Sort data by name, then if data.children is an array with
    // more than 1 element, sort children too by name

    data = data.sort(function (a: RoomResourceDetail, b: RoomResourceDetail) {
      try {
        if (a && a?.name && b && b?.name) {
          let NameA = a?.name;
          let NameB = b?.name;
          if (NameA < NameB) {
            return -1;
          }
          if (NameA > NameB) {
            return 1;
          }
        }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        /* squash */
      }
      // names must be equal or documents incorrect
      return 0;
    });

    // Sort the children of each element by their name attribute
    data.forEach((room: RoomResourceDetail) => {
      if (room?.children && room?.children?.length > 0) {
        room.children.sort((a: RoomResourceDetail, b: RoomResourceDetail) =>
          a?.name.localeCompare(b?.name)
        );
      }
    });

    return data;
  };

  // Decorate as required
  const preProcessRoomList = (classdata: any) => {
    if (isArray(classdata)) {
      return classdata.map((c: any) => {
        return c;
      });
    } else {
      return [];
    }
  };

  return {
    isError,
    error,
    myRoomList,
    refreshMyRoomList,
    myRoomListLoaded,
  };
}
